import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Fuel, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { addBookingCharge, deleteBookingCharge } from "@/lib/booking-charges.functions";

export type BookingCharge = {
  id: string;
  label: string;
  amount_cents: number;
  note: string | null;
  charge_date: string | null;
  created_at: string;
};

const todayKey = () => new Date().toISOString().slice(0, 10);

/** Which night a charge belongs to — falls back to the day it was entered. */
const chargeNight = (c: BookingCharge) => (c.charge_date ?? c.created_at).slice(0, 10);

const prettyNight = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const PRESETS = ["Fuel", "Ice", "Damage", "Late checkout", "Fish processing", "Other"];

/**
 * Extra charges staff add after a stay is booked — fuel, ice, damage and other
 * miscellaneous items. They show up on the guest's own page at /my-stay.
 */
export function BookingChargesPanel({ bookingId }: { bookingId: string }) {
  const [charges, setCharges] = useState<BookingCharge[]>([]);
  const [label, setLabel] = useState("Fuel");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [night, setNight] = useState(todayKey);
  const [busy, setBusy] = useState(false);

  const addCharge = useServerFn(addBookingCharge);
  const removeCharge = useServerFn(deleteBookingCharge);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("booking_charges")
      .select("id,label,amount_cents,note,charge_date,created_at")
      .eq("booking_id", bookingId)
      .order("charge_date", { ascending: true, nullsFirst: true })
      .order("created_at", { ascending: true });
    setCharges((data ?? []) as BookingCharge[]);
  }, [bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  const total = charges.reduce((sum, c) => sum + c.amount_cents, 0);

  // Grouped per night so staff can see what a party ran up each day.
  const nights = (() => {
    const map = new Map<string, BookingCharge[]>();
    for (const c of charges) {
      const day = chargeNight(c);
      map.set(day, [...(map.get(day) ?? []), c]);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  })();

  async function onAdd(event: React.FormEvent) {
    event.preventDefault();
    const dollars = Number(amount);
    if (!label.trim() || !Number.isFinite(dollars) || dollars === 0) {
      toast.error("Enter a description and an amount.");
      return;
    }
    setBusy(true);
    try {
      await addCharge({
        data: {
          booking_id: bookingId,
          label: label.trim(),
          amount_cents: Math.round(dollars * 100),
          note: note.trim() || null,
          charge_date: night || null,
        },
      });
      setAmount("");
      setNote("");
      await load();
      toast.success("Charge added to the guest's stay.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not add that charge.");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(id: string) {
    setBusy(true);
    try {
      await removeCharge({ data: { id } });
      await load();
      toast.success("Charge removed.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove that charge.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Fuel &amp; other charges
      </span>

      {charges.length > 0 && (
        <div className="mt-2 space-y-3">
          {nights.map(([day, lines]) => (
            <div key={day}>
              <p className="flex justify-between px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span>{prettyNight(day)}</span>
                <span className="tabular-nums">
                  {money(lines.reduce((sum, l) => sum + l.amount_cents, 0))}
                </span>
              </p>
              <ul className="mt-1 space-y-1 text-sm">
                {lines.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-1.5"
                  >
                    <span>
                      {c.label}
                      {c.note ? <span className="text-xs text-muted-foreground"> — {c.note}</span> : null}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="tabular-nums">{money(c.amount_cents)}</span>
                      <button
                        type="button"
                        onClick={() => void onRemove(c.id)}
                        disabled={busy}
                        aria-label={`Remove ${c.label}`}
                        className="text-destructive hover:opacity-80 disabled:opacity-50"
                      >
                        <Trash2 size={14} aria-hidden />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="flex justify-between border-t border-border/60 px-3 pt-2 text-sm font-semibold">
            <span>Extra charges total</span>
            <span className="tabular-nums">{money(total)}</span>
          </p>
        </div>
      )}

      <form onSubmit={onAdd} className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-xs">
          <span className="mb-1 block text-muted-foreground">Charge</span>
          <input
            list={`charge-presets-${bookingId}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <datalist id={`charge-presets-${bookingId}`}>
            {PRESETS.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </label>
        <label className="text-xs">
          <span className="mb-1 block text-muted-foreground">Amount ($)</span>
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="120.00"
            className="w-28 rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="mb-1 block text-muted-foreground">Night</span>
          <input
            type="date"
            value={night}
            onChange={(e) => setNight(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs flex-1 min-w-40">
          <span className="mb-1 block text-muted-foreground">Note (optional)</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="42 gal, Miss Bonnie"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs hover:bg-muted disabled:opacity-60"
        >
          <Plus size={13} aria-hidden /> Add charge
        </button>
      </form>
      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Fuel size={12} aria-hidden /> Charges appear on the guest's own page at /my-stay with their
        confirmation code.
      </p>
    </div>
  );
}
